import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Package, Search, Loader } from 'lucide-react';

const ProductSelector = ({ selectedProduct, onProductSelect, isAnalyzing }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      setProducts(data.products || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(product =>
    product.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6">
      <h3 className="text-white font-semibold text-lg mb-4 flex items-center gap-2">
        <Package size={20} className="text-cyan-400" />
        Sélectionner un produit
      </h3>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
        <input
          type="text"
          placeholder="Rechercher un produit..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 transition-all"
          data-testid="product-search"
        />
      </div>

      {/* Products List */}
      <div className="space-y-2 max-h-96 overflow-y-auto custom-scrollbar">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader className="animate-spin text-cyan-400" size={24} />
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            {searchTerm ? 'Aucun produit trouvé' : 'Aucun produit disponible'}
          </div>
        ) : (
          filteredProducts.map((product) => (
            <motion.button
              key={product.id}
              whileHover={{ scale: 1.02, x: 4 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onProductSelect(product)}
              className={`w-full text-left p-4 rounded-xl border transition-all duration-200 ${
                selectedProduct?.id === product.id
                  ? 'bg-cyan-500/20 border-cyan-400 shadow-lg shadow-cyan-500/10'
                  : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20'
              }`}
              data-testid={`product-item-${product.id}`}
            >
              <div className="flex items-start gap-3">
                {product.images && product.images[0] ? (
                  <img
                    src={product.images[0]}
                    alt={product.name}
                    className="w-12 h-12 object-cover rounded-lg"
                  />
                ) : (
                  <div className="w-12 h-12 bg-slate-700 rounded-lg flex items-center justify-center">
                    <Package size={20} className="text-slate-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-white font-medium truncate">{product.name}</div>
                  {product.description && (
                    <div className="text-slate-400 text-sm truncate">{product.description}</div>
                  )}
                  {product.price && (
                    <div className="text-cyan-400 text-sm font-semibold mt-1">{product.price}€</div>
                  )}
                </div>
              </div>
            </motion.button>
          ))
        )}
      </div>

      {isAnalyzing && (
        <div className="mt-4 flex items-center gap-2 text-cyan-400 text-sm">
          <Loader className="animate-spin" size={16} />
          <span>Analyse IA en cours...</span>
        </div>
      )}
    </div>
  );
};

export default ProductSelector;
